import { type ValueEvent } from '@adornis/base/utilTypes.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import type { ColumnDefinition } from '@adornis/data-view/ColumnDefinition.js';
import type { DataViewOptions } from '@adornis/data-view/client/data-view-options.js';
import { View } from '@adornis/data-view/db/view.js';
import { FormField } from '@adornis/formfield/form-field.js';
import { LearningContentAPI } from '@chancenwerk/campus-learning-content/api/LearningContentAPI.js';
import { CampusEvent } from '@chancenwerk/campus-learning-content/entities/CampusEvent.js';
import { CampusEventFilter } from '@chancenwerk/campus-learning-content/entities/CampusEventFilter.js';
import { ChromiumDialog } from '@chancenwerk/design/components/cr-dialog.js';
import { html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { takeUntil } from 'rxjs';
import { CampusRoutes } from '../../routes.js';

@customElement('campus-formfield-event-list')
export class CampusFormFieldEventList extends FormField<string[]> {
  @property({ attribute: false }) area = new RXController(this, '');

  @state() private _filter = new CampusEventFilter({ eventIDs: this.value.value ?? [] });

  private readonly _columnDefinitions: ColumnDefinition<typeof CampusEvent>[] = [
    {
      columnName: 'Titel',
      fieldName: 'title',
      columnWidth: '1fr',
      getFieldMapping(entityInstance) {
        return entityInstance.title;
      },
    },
    {
      customID: 'date',
      columnName: 'Datum',
      columnWidth: '1fr',
      getFieldMapping(entityInstance) {
        return `${entityInstance.startDate?.toFormat('dd LLL yyyy')} - ${entityInstance.endDate?.toFormat(
          'dd LLL yyyy',
        )}`;
      },
    },
    {
      customID: 'action',
      columnName: '',
      columnWidth: 'max-content',
      renderItem: event => {
        return html`
          <cr-flex center crossaxis-end>
            <cr-button
              mode="fab"
              icon="edit"
              small
              href="${CampusRoutes.eventAdmin + `/edit/${event._id}`}"
              target="_blank"
            >
            </cr-button>
          </cr-flex>
        `;
      },
    },
    {
      customID: 'action2',
      columnName: '',
      columnWidth: 'max-content',
      renderItem: event => {
        return html`
          <cr-flex center crossaxis-end>
            <cr-button
              mode="fab"
              icon="delete"
              tone="error"
              small
              @click=${async () => {
                if (
                  !(await ChromiumDialog.confirm(
                    'Das Event wirklich löschen? Die Gruppe wird daraufhin automatisch gespeichert und das Event ist dauerhaft gelöscht.',
                    { highlightDecline: true, acceptText: 'Löschen', declineText: 'Abbrechen' },
                  ))
                ) {
                  return;
                }
                let oldValue = this.value.value;
                const index = oldValue?.findIndex(id => id === event._id);
                if (index === undefined) throw new Error('No index found');

                oldValue = oldValue?.splice(index, 1);
                this.value.next(oldValue);
                await event.remove();
                const eventDis = new CustomEvent('value-picked', {
                  detail: {
                    value: this.value.value,
                  },
                });
                this.dispatchEvent(eventDis);
              }}
            >
            </cr-button>
          </cr-flex>
        `;
      },
    },
  ];

  override styles() {
    return [
      {
        ':host': {
          height: '100%',
          width: '100%',
        },
      },
    ];
  }

  override connectedCallback(): void {
    super.connectedCallback();
    this.value.observable.pipe(takeUntil(this.disconnected)).subscribe(eventIDs => {
      if (eventIDs && eventIDs.length > 0) this._filter = new CampusEventFilter({ eventIDs });
      else this._filter = new CampusEventFilter({ eventIDs: ['keine_ids'] });
    });
  }

  override render() {
    return html`
      <cr-flex space="md" w100 h100>
        <cr-flex
          space="sm"
          ${css({
            position: 'sticky',
            top: '0',
            zIndex: '1',
          })}
        >
          <cr-text> Events auswählen </cr-text>
          <cr-flex horizontal flex space="md">
            <cr-input
              ${css({ selfAlign: 'center', justifySelf: 'center', background: 'white' })}
              @value-changed="${(e: ValueEvent<string>) => {
                if (this._filter.search !== e.detail.value) this._filter.search = e.detail.value;
              }}"
              placeholder="Suche"
              icon="search"
              flex
            ></cr-input>
            <cr-button mode="outline" icon="add" href="${CampusRoutes.eventAdmin + `/edit`}" target="_blank">
              Event erstellen
            </cr-button>
          </cr-flex>
        </cr-flex>
        <x-data-view
          flex
          ${css({ overflow: 'auto' })}
          .options=${{
            class: CampusEvent,
            operation: LearningContentAPI.subscribeEventsByFilter,
            view: new View({ filter: this._filter }),
            selectionSet: CampusEvent.allFields,
            onlyShowLastTranslation: true,
            customColumns: this._columnDefinitions,
          } as DataViewOptions<typeof CampusEvent>}
        >
        </x-data-view>
      </cr-flex>
    `;
  }
}
